<script setup lang="ts">
import { computed, ref, useSlots, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { DocumentAddIcon, PenNewSquareIcon } from '@solar-icons/vue/linear'
import AppNavGroup from './AppNavGroup.vue'
import AppNavItem from './AppNavItem.vue'
import AppNavBrand from './AppNavBrand.vue'
import AppNavThemeButton from './AppNavThemeButton.vue'
import AppNavUser from './AppNavUser.vue'
import type { NavBadge, NavBrand, NavUser } from './nav-types'
import SetupStepList from '../../setup/SetupStepList/SetupStepList.vue'
import RaldoMark from '../../primitives/RaldoMark/RaldoMark.vue'
import AppButton from '../../primitives/AppButton/AppButton.vue'
import AppMenu from '../../primitives/AppMenu/AppMenu.vue'
import type { MenuItem } from '../../primitives/AppMenu/menu-types'
import AppScrollArea from '../../primitives/AppScrollArea/AppScrollArea.vue'
import AppTooltip from '../../primitives/AppTooltip/AppTooltip.vue'
import AppThemeSwitch from '../../primitives/AppThemeSwitch/AppThemeSwitch.vue'
import { useCreateContentStore } from '../../../stores/create-content'
import { newPostRoute } from '../../../router/post-route'
import { vCoach } from '../../tour/v-coach'
import { NAV_ITEMS, type NavLink } from './nav-items'
import { useSidebar } from './useSidebar'
import type { StepListItem } from '../../setup/SetupStepList/step-list-types'
import {
  type OnboardingChainStepView,
  stepDescriptionKey,
  stepTitleKey,
} from '@/types/onboarding-chain'
import { useHandoverSequence } from '@/composables/useHandoverSequence'
import { useTravellingHighlight } from '@/composables/useTravellingHighlight'

const props = withDefaults(
  defineProps<{
    user?: NavUser | null
    brand?: NavBrand | null
    /** Counts keyed by nav item id, shown as a badge on the matching item. */
    navBadges?: Record<string, NavBadge>
    /** Setup mode: the chain's steps take the place of the create button, the nav and the chats. */
    setup?: boolean
    /** The steps to list in setup mode, in chain order. */
    setupSteps?: readonly OnboardingChainStepView[]
    /** Turns on while setup hands over to Home, which lifts the setup steps away instead of dropping them. */
    handover?: boolean
    /** The theme on screen now; the switch at the foot of the nav shows it. */
    theme?: 'light' | 'dark'
  }>(),
  { user: null, brand: null, setup: false, setupSteps: () => [], handover: false, theme: 'light' },
)

const emit = defineEmits<{
  'sign-out': []
  'update:theme': [value: 'light' | 'dark']
}>()

const { activeId, isMobile, setOpenMobile, isGroupOpen, state } = useSidebar()
const rail = computed(() => state.value === 'rail')
const slots = useSlots()
const { t } = useI18n()
const router = useRouter()
const createContent = useCreateContentStore()
const stepItems = computed<StepListItem[]>(() =>
  props.setupSteps.map((entry) => ({
    id: entry.step,
    index: entry.index,
    state: entry.state,
    fresh: entry.fresh,
    title: t(stepTitleKey(entry.step)),
    caption: t(stepDescriptionKey(entry.step)),
  })),
)
const createMenuItems = computed<MenuItem[]>(() => [
  { id: 'post', label: t('content.create.newPost'), icon: PenNewSquareIcon },
  { id: 'page', label: t('content.create.newPage'), icon: DocumentAddIcon },
])

function onCreate(id: string): void {
  if (id === 'post') void router.push(newPostRoute())
  else createContent.showPageStep()
  closeMobileSheet()
}

const handoverSequence = useHandoverSequence()

function leaveStepList(element: Element, done: () => void): void {
  if (!props.handover || !(element instanceof HTMLElement)) {
    done()
    return
  }
  void handoverSequence.leaveSteps(element).then(done)
}

const navContent = ref<HTMLElement | null>(null)

const activeGroup = computed(() =>
  NAV_ITEMS.find((group) => group.rows.some((row) => row.id === activeId.value)),
)
const highlightShown = computed(() => {
  const group = activeGroup.value
  return group !== undefined && (rail.value || !group.labelKey || isGroupOpen(group.id))
})

const {
  style: highlightStyle,
  travels,
  tracking,
  landThenTravel,
} = useTravellingHighlight({
  container: () => navContent.value,
  target: () => navContent.value?.querySelector<HTMLElement>('[aria-current="page"]') ?? null,
  track: () => activeId.value,
  observe: () => navContent.value,
  suspendWhileMoving: true,
})

watch(navContent, (element) => {
  if (element) landThenTravel()
})

function select(row: NavLink) {
  void router.push(row.to)
  closeMobileSheet()
}

function closeMobileSheet() {
  if (isMobile.value) setOpenMobile(false)
}

function coachKey(id: string): string {
  if (id === 'identity') return 'brand.foundation'
  if (id === 'style') return 'brand.style'
  return ''
}
</script>

<template>
  <div class="icon-sm relative flex h-full w-full flex-col">
    <div
      data-arrival="brand"
      class="relative mb-2 flex items-center gap-4 px-6 pt-8"
      :class="[setup ? 'h-24' : undefined, rail ? 'flex-col' : undefined]"
    >
      <RaldoMark v-if="setup" size="xs" wordmark class="ml-2" />
      <div v-else class="min-w-0" :class="rail ? 'w-full' : 'flex-1'">
        <AppNavBrand :brand="brand" :user="user" @sign-out="emit('sign-out')" />
      </div>

      <div v-if="!setup" data-arrival="create" class="flex shrink-0">
        <AppMenu :items="createMenuItems" placement="bottom-end" @select="onCreate">
          <template #trigger="{ aria: menuAria }">
            <AppTooltip
              :content="t('content.create.button')"
              :placement="rail ? 'right' : 'bottom'"
            >
              <template #trigger="{ aria }">
                <AppButton
                  size="sm"
                  variant="ghost-strong"
                  icon-only
                  :start-icon="PenNewSquareIcon"
                  :aria-label="t('content.create.button')"
                  v-bind="{ ...aria, ...menuAria }"
                />
              </template>
            </AppTooltip>
          </template>
        </AppMenu>
      </div>
    </div>

    <Transition :css="false" @leave="leaveStepList">
      <SetupStepList v-if="setup" :steps="stepItems" :aria-label="t('setup.stepList')" />
    </Transition>

    <div v-if="!setup" class="flex shrink-0 px-6 py-2" aria-hidden="true">
      <span class="mx-4 h-px flex-1 bg-border" />
    </div>

    <AppScrollArea
      v-if="!setup"
      v-coach="'shell.nav'"
      as="nav"
      class="min-h-0 px-6 py-2"
      :aria-label="t('shell.primaryNav')"
    >
      <div ref="navContent" class="relative isolate flex flex-col gap-8">
        <span
          aria-hidden="true"
          :class="[
            'cursor-glass pointer-events-none absolute inset-x-0 top-0 -z-1 rounded-4 motion-reduce:transition-none',
            travels && !tracking
              ? 'travelling-highlight'
              : 'transition-opacity duration-moderate ease-spring',
            highlightShown ? 'opacity-100' : 'opacity-0',
          ]"
          :style="highlightStyle"
        />
        <AppNavGroup
          v-for="group in NAV_ITEMS"
          :id="group.id"
          :key="group.id"
          :label="group.labelKey ? t(group.labelKey) : undefined"
          :holds-active="group.rows.some((row) => row.id === activeId)"
        >
          <AppNavItem
            v-for="(row, index) in group.rows"
            :key="row.id"
            v-coach="coachKey(row.id)"
            :label="t(row.labelKey)"
            :icon="row.icon"
            :index="index"
            :active="row.id === activeId"
            :badge="navBadges?.[row.id]"
            @select="select(row)"
          />
        </AppNavGroup>
      </div>
    </AppScrollArea>

    <div data-arrival="footer" class="mt-auto flex flex-col gap-4 px-6 pb-6 pt-4">
      <AppNavUser v-if="setup" :user="user" @sign-out="emit('sign-out')" />
      <template v-else>
        <div v-if="slots['nav-footer']" data-arrival="hub">
          <slot name="nav-footer" />
        </div>
        <div class="flex" :class="rail ? 'justify-center' : 'px-4'">
          <AppNavThemeButton
            v-if="rail"
            :theme="theme"
            @update:theme="emit('update:theme', $event)"
          />
          <AppThemeSwitch v-else :theme="theme" @update:theme="emit('update:theme', $event)" />
        </div>
      </template>
    </div>
  </div>
</template>
